/**
 * Answer missingness for browser aggregation.
 *
 * The sentinel list is generated from the Python owner
 * (`MISSING_ANSWER_SENTINELS` in apps/python-api/canonical_analysis/runtime.py);
 * this module only applies it the way the owner does. Both sides are held to
 * `tests/fixtures/reporting/missingness.v1.json`.
 *
 * - null/undefined and a sentinel string (trimmed, lower-cased) are missing.
 * - A declared categorical option whose exact stored value equals a sentinel
 *   spelling ("None", "N/A") is an answer, not missing.
 * - Zero, `false` and every other value are answers.
 */
import { MISSING_ANSWER_SENTINELS } from './missingness.generated'

const SENTINELS: ReadonlySet<string> = new Set(MISSING_ANSWER_SENTINELS)

/** Whether a saved scalar answer is not usable: null, undefined or a sentinel. */
export function isMissingAnswerSentinel(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true
  }
  if (typeof value !== 'string') {
    return false
  }
  return SENTINELS.has(value.trim().toLowerCase())
}

/** Whether `value` is exactly one of the declared option values. */
export function declaresAnswerValue(value: unknown, declaredValues: ReadonlyArray<unknown>): boolean {
  if (typeof value !== 'string') {
    return false
  }
  return declaredValues.some((declared) => declared === value)
}

/** `isMissingAnswerSentinel`, except that an exact declared option is an answer. */
export function isMissingCategoricalAnswer(
  value: unknown,
  declaredValues: ReadonlyArray<unknown>,
): boolean {
  if (!isMissingAnswerSentinel(value)) {
    return false
  }
  return !declaresAnswerValue(value, declaredValues)
}
